// src/hooks/useRecentTransactions.ts
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useCollection } from "@amzn/awsui-collection-hooks";
import useFetchRecentTransactions from "./useFetchRecentTransactions";
import type { Transaction } from "../lusca-search-api/generated-src";
import type { SearchFilters } from "../types/common";
import {
  buildApiParams,
  DEFAULT_FILTERS,
  getEndTime,
  parseEndTimeFromUrl,
  parseFiltersFromUrl,
  serializeFilterState,
} from "../helpers/recentTransactionsUtils";
import {
  getGlLabel,
  TABLE_PREFERENCES,
} from "../config/recentTransactionTableConfig";

export function useRecentTransactions() {
  const [urlParams, setUrlParams] = useSearchParams();

  // parse URL params once on mount
  const initialFilters = useMemo(
    () => parseFiltersFromUrl(urlParams),
    [urlParams]
  );
  const initialEndTime = useMemo(
    () => parseEndTimeFromUrl(urlParams),
    [urlParams]
  );
  const initialPersistedEndTime =
    initialFilters.timeRange.value === "custom" ? initialEndTime : undefined;

  const [selectedFilters, setSelectedFilters] =
    useState<SearchFilters>(initialFilters);

  const [baseParams, setBaseParams] = useState(() => {
    const params = buildApiParams(initialFilters);
    if (initialPersistedEndTime !== undefined) {
      params.endTime = initialPersistedEndTime;
    }
    return params;
  });

  const [pageEndTime, setPageEndTime] = useState<number | undefined>(
    initialPersistedEndTime ??
      getEndTime(initialFilters.timeRange, initialFilters.date)
  );
  const [fetchedPageCount, setFetchedPageCount] = useState(1);

  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);

  const [searchToken, setSearchToken] = useState(0);
  const [activeToken, setActiveToken] = useState(0);

  // reset everything when a new search is triggered

  useEffect(() => {
    setAllTransactions([]);
    setFetchedPageCount(1);
    setSearchToken((t) => t + 1);
  }, [baseParams]);

  const apiParams = useMemo(
    () => ({ ...baseParams, endTime: pageEndTime }),
    [baseParams, pageEndTime]
  );
  const {
    transactions,
    nextPageTimestamp,
    executedQuery,
    logGroups,
    isLoading,
    error,
    retrySearch,
  } = useFetchRecentTransactions(apiParams);

  // merge in fetched transactions
  useEffect(() => {
    if (!isLoading) {
      setAllTransactions((prev) => [
        ...prev,
        ...transactions.filter((t) => !prev.some((x) => x.id === t.id)),
      ]);
      setActiveToken(searchToken);
    }
  }, [transactions, isLoading, fetchedPageCount, searchToken]);

  const isDataLoading = searchToken !== activeToken || isLoading;

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(allTransactions, {
    filtering: {
      filteringFunction: (item, text) => {
        if (!text) return true;
        const ft = text.toLowerCase();
        return [
          item.id,
          item.stage,
          item.country,
          item.gl,
          getGlLabel(item.gl),
          item.usecase,
          item.paymentMethod,
          item.consumptionType,
          new Date(item.processedTime).toLocaleString(),
        ].some((v) => String(v).toLowerCase().includes(ft));
      },
    },
    pagination: { pageSize: TABLE_PREFERENCES.pageSize },
    sorting: {
      defaultState: {
        sortingColumn: { sortingField: "processedTime" },
        isDescending: true,
      },
    },
  });

  const pagesCount = (() => {
    const isFiltering = !!filterProps.filteringText;
    if (isFiltering) {
      return (
        Math.ceil((filteredItemsCount || 0) / TABLE_PREFERENCES.pageSize) || 1
      );
    }
    if (isLoading && fetchedPageCount === 1) {
      return 2;
    }
    return nextPageTimestamp ? fetchedPageCount + 1 : fetchedPageCount;
  })();

  const customPaginationProps = {
    ...paginationProps,
    pagesCount,
    openEnd: !filterProps.filteringText,
    disabled: isLoading,
    onChange: ({ detail }: { detail: { currentPageIndex: number } }) => {
      const newPage = detail.currentPageIndex;
      actions.setCurrentPage(newPage);
      if (
        !filterProps.filteringText &&
        nextPageTimestamp &&
        newPage > fetchedPageCount
      ) {
        setFetchedPageCount(newPage);
        setPageEndTime(nextPageTimestamp);
      }
    },
  };

  const handleSearch = useCallback(
    (overrides?: Partial<SearchFilters>) => {
      const filters = overrides
        ? { ...selectedFilters, ...overrides }
        : selectedFilters;
      setSelectedFilters(filters);
      const params = buildApiParams(filters);
      setBaseParams(params);
      setUrlParams(serializeFilterState(filters));
      setPageEndTime(params.endTime);
    },
    [selectedFilters, setUrlParams]
  );

  // Clear filters also turns deep search off, including in the URL, so the
  // next search is a normal one.
  const clearFilter = useCallback(() => {
    actions.setFiltering("");
    setSelectedFilters(DEFAULT_FILTERS);
    if (urlParams.has("deep")) {
      const next = new URLSearchParams(urlParams);
      next.delete("deep");
      setUrlParams(next, { replace: true });
    }
  }, [actions, urlParams, setUrlParams]);

  return {
    items,
    actions,
    collectionProps,
    filterProps,
    paginationProps: customPaginationProps,
    handleSearch,
    clearFilter,
    selectedFilters,
    setSelectedFilters,
    isLoading: isDataLoading,
    executedQuery,
    logGroups,
    error,
    retrySearch,
  };
}
